type AnalyticsData = Record<string, string | number | boolean>

type UmamiProperties = Record<string, unknown>

interface UmamiTracker {
  track(eventName: string, data?: AnalyticsData): void
  track(transform: (properties: UmamiProperties) => UmamiProperties): void
}

declare global {
  interface Window {
    umami?: UmamiTracker
    __SOV_PRERENDER__?: boolean
  }
}

type PendingCall =
  | { kind: 'pageview'; path: string }
  | { kind: 'event'; name: string; data?: AnalyticsData }

const DEFAULT_SRC = 'https://stats.joinsov.com/script.js'
const WEBSITE_ID = import.meta.env.VITE_UMAMI_WEBSITE_ID?.trim() ?? ''
const SCRIPT_SRC = import.meta.env.VITE_UMAMI_SRC?.trim() || DEFAULT_SRC
const HOST_URL = import.meta.env.VITE_UMAMI_HOST?.trim() ?? ''

let initialized = false
let loaded = false
const pending: PendingCall[] = []

function enabled(): boolean {
  return Boolean(WEBSITE_ID) && typeof window !== 'undefined' && !window.__SOV_PRERENDER__
}

function normalizePath(path: string): string {
  const withoutHashPrefix = path.startsWith('#') ? path.slice(1) : path
  const pathname = withoutHashPrefix.split(/[?#]/, 1)[0] || '/'
  const withLeadingSlash = pathname.startsWith('/') ? pathname : `/${pathname}`
  return withLeadingSlash !== '/' && withLeadingSlash.endsWith('/') ? withLeadingSlash.slice(0, -1) : withLeadingSlash
}

function send(call: PendingCall): boolean {
  const tracker = window.umami
  if (!tracker) return false

  try {
    if (call.kind === 'pageview') {
      tracker.track((properties) => ({ ...properties, url: call.path }))
    } else {
      tracker.track(call.name, call.data)
    }
    return true
  } catch {
    return false
  }
}

function flush(): void {
  if (!window.umami) return
  loaded = true

  while (pending.length > 0) {
    const call = pending.shift()
    if (call && !send(call)) {
      pending.unshift(call)
      loaded = false
      return
    }
  }
}

export function initAnalytics(): void {
  if (initialized || !enabled()) return
  initialized = true

  const script = document.createElement('script')
  script.defer = true
  script.src = SCRIPT_SRC
  script.dataset.websiteId = WEBSITE_ID
  script.dataset.autoTrack = 'false'
  script.dataset.autoPageview = 'false'
  script.dataset.doNotTrack = 'true'
  if (HOST_URL) script.dataset.hostUrl = HOST_URL

  script.addEventListener('load', () => {
    if (!pending.some((call) => call.kind === 'pageview')) {
      pending.unshift({ kind: 'pageview', path: normalizePath(window.location.pathname) })
    }
    flush()
  })
  document.head.appendChild(script)
}

export function trackPageview(path: string): void {
  if (!enabled()) return
  const call: PendingCall = { kind: 'pageview', path: normalizePath(path) }
  if (!loaded || !send(call)) pending.push(call)
}

export function trackEvent(name: string, data?: AnalyticsData): void {
  if (!enabled()) return
  const call: PendingCall = { kind: 'event', name, data }
  if (!loaded || !send(call)) pending.push(call)
}
